import { Button } from "@win-reality/react-ui";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";

import { setTaggingFlowStep } from "../../../../store/slices/hotkeys";
import { resetAllTags } from "../../../../store/slices/session";
import {
  incrementQueueLength,
  resetQueueLength,
  setFocusedEventPayload,
  setIsViewingPreviousEvent,
} from "../../../../store/slices/socket";
import {
  selectEventQueue,
  selectRecentEvent,
} from "../../../../store/slices/socket/selectors";
import type { RootState } from "../../../../store/store";
import type { IQueueEvent } from "../../../../store/types";
import { eventTime } from "../../../../store/utils/eventTime";
import { secondsSinceEvent } from "../../../../store/utils/secondsSinceEvent";
import { EventCard } from "./EventCard";

/**
 * EventQueue - Displays current and previous events
 *
 * Shows:
 * - Current Event (untagged, waiting for tags)
 * - Previous Events list (scrollable)
 * - Show More button to expand queue
 *
 * Features:
 * - Click current event to return to tagging it
 * - Click previous events to view/edit them
 * - Scroll area for long event lists
 * - Visual indicators for active event
 */
export const EventQueue = () => {
  const dispatch = useDispatch();
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const active = !isViewingPreviousEvent;
  const eventQueue = useSelector(selectEventQueue);
  const recentEvent = useSelector(selectRecentEvent);
  const { queueLength } = useSelector((state: RootState) => state.socket);

  const handleEmptyStateClick = () => {
    dispatch(setIsViewingPreviousEvent(false));
    dispatch(setFocusedEventPayload({}));
    dispatch(resetQueueLength());
    dispatch(resetAllTags({}));
    dispatch(setTaggingFlowStep(1));
  };

  const handleCurrentEventClick = () => {
    dispatch(setIsViewingPreviousEvent(false));
    dispatch(setFocusedEventPayload({}));
    dispatch(resetQueueLength());
    dispatch(resetAllTags({}));
    dispatch(setTaggingFlowStep(0));
  };

  return (
    <div className="flex flex-1 flex-col rounded bg-white shadow-md p-2 h-96 overflow-hidden">
      {/* Scrollable content */}
      <div className="h-full overflow-y-auto">
        {/* Current Event Divider */}
        <div className="mb-3 flex items-center">
          <div className="flex-1 border-t border-dashed border-gray-300"></div>
          <span className="px-3 text-xs font-semibold uppercase text-accent-dark-3">
            Current Event
          </span>
          <div className="flex-1 border-t border-dashed border-gray-300"></div>
        </div>

        {/* Current Event Card */}
        {Object.keys(recentEvent).length === 0 ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            onClick={handleEmptyStateClick}
            className={clsx(
              // Size
              "h-[60px]",
              // Layout
              "flex items-center justify-center",
              // Spacing
              "mx-2 mb-4 p-3",
              // Border
              "rounded",
              // States
              "cursor-pointer",
              active
                ? "border border-blue bg-[hsla(218,100%,52%,0.05)] shadow-[0_0_8px_#0066ff66]"
                : "border border-dashed border-gray-300 bg-white shadow-sm hover:bg-[hsla(218,100%,52%,0.05)]",
            )}
          >
            <span className="text-sm">Waiting for next event...</span>
          </div>
        ) : (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            onClick={handleCurrentEventClick}
            className={clsx(
              // Size
              "h-[60px]",
              // Layout
              "flex items-center justify-between",
              // Spacing
              "mx-2 mb-4 p-3",
              // Border
              "rounded border",
              // States
              "cursor-pointer",
              active
                ? "border-blue bg-[hsla(218,100%,52%,0.05)] shadow-[0_0_8px_#0066ff66]"
                : "border-transparent bg-white shadow-sm hover:bg-[hsla(218,100%,52%,0.05)]",
            )}
          >
            <div className="flex flex-col">
              <span className="text-sm font-semibold">
                {recentEvent?.pitch_data?.RelSpeedMPH?.toFixed(1) || "---"}
              </span>
              <span className="text-xs text-gray-500">Pitch</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-sm font-semibold">Untagged</span>
              <span className="text-xs text-gray-500">
                {secondsSinceEvent(eventTime(recentEvent))}
              </span>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-sm font-semibold">
                {recentEvent?.hit_data?.ExitSpeedMPH?.toFixed(1) ?? "---"}
              </span>
              <span className="text-xs text-gray-500">EV</span>
            </div>
          </div>
        )}

        {/* Previous Events Divider */}
        <div className="my-3 flex items-center">
          <div className="flex-1 border-t border-dashed border-gray-300"></div>
          <span className="px-3 text-xs font-semibold uppercase text-accent-dark-3">
            Previous Events
          </span>
          <div className="flex-1 border-t border-dashed border-gray-300"></div>
        </div>

        {/* Previous Events List */}
        {Object.keys(recentEvent).length === 0 && eventQueue.length > 0 && (
          <EventCard event={eventQueue[0]} />
        )}
        {eventQueue
          .slice(1, queueLength)
          .map((event: IQueueEvent, index: number) => (
            <EventCard key={index} event={event} />
          ))}

        {/* Show More Button */}
        {eventQueue.length > queueLength && (
          <div className="flex justify-center pt-2 pb-4">
            <Button
              onClick={() => dispatch(incrementQueueLength())}
              size="sm"
              variant="secondary-flat-light"
            >
              Show More
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
